/**
 * Lektor TTS PL — jeden głos naraz, pliki ogg + fallback speechSynthesis.
 * Hover: debounce 300 ms (UiKit / InteractiveObject w G15).
 */
import {
  catalogKeyForText,
  catalogText,
  normalizeLabel,
  ttsUrl,
} from './ttsCatalog.js';

const STORAGE_KEY = 'pieskowo.lektor';
export const NARRATOR_HOVER_DEBOUNCE_MS = 300;

export type SayOpts = {
  /** Pomiń debounce (pointerdown / jawne wywołanie). Default false = debounced. */
  immediate?: boolean;
};

type AudioLike = {
  pause: () => void;
  play: () => Promise<void>;
  currentTime: number;
  src: string;
  onended: ((this: GlobalEventHandlers, ev: Event) => unknown) | null;
  onerror: ((this: GlobalEventHandlers, ev: Event | string) => unknown) | null;
};

export type NarratorDeps = {
  createAudio?: (url: string) => AudioLike;
  speech?: {
    speak: (text: string, lang: string) => void;
    cancel: () => void;
  };
  storage?: {
    getItem: (k: string) => string | null;
    setItem: (k: string, v: string) => void;
  };
  now?: () => number;
  setTimeout?: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout?: (id: ReturnType<typeof setTimeout>) => void;
};

function defaultCreateAudio(url: string): AudioLike {
  const a = new Audio(url);
  a.preload = 'auto';
  return a;
}

function defaultSpeech() {
  return {
    speak(text: string, lang: string): void {
      if (typeof window === 'undefined' || !window.speechSynthesis) return;
      const u = new SpeechSynthesisUtterance(text);
      u.lang = lang;
      const voices = window.speechSynthesis.getVoices();
      const pl =
        voices.find((v) => v.lang.toLowerCase().startsWith('pl')) ??
        voices.find((v) => /polski|polish/i.test(v.name));
      if (pl) u.voice = pl;
      u.rate = 0.95;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(u);
    },
    cancel(): void {
      if (typeof window === 'undefined' || !window.speechSynthesis) return;
      window.speechSynthesis.cancel();
    },
  };
}

function defaultStorage() {
  return {
    getItem(k: string): string | null {
      try {
        return localStorage.getItem(k);
      } catch {
        return null;
      }
    },
    setItem(k: string, v: string): void {
      try {
        localStorage.setItem(k, v);
      } catch {
        /* ignore */
      }
    },
  };
}

/** Klucze, dla których wiemy że pliku nie ma (unikamy spam 404). */
const missingFiles = new Set<string>();

export class Narrator {
  private lektorOn = true;
  private currentAudio: AudioLike | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private pendingToken = 0;
  private readonly createAudio: (url: string) => AudioLike;
  private readonly speech: NonNullable<NarratorDeps['speech']>;
  private readonly storage: NonNullable<NarratorDeps['storage']>;
  private readonly setTimeoutFn: NonNullable<NarratorDeps['setTimeout']>;
  private readonly clearTimeoutFn: NonNullable<NarratorDeps['clearTimeout']>;
  /** Ostatni znormalizowany tekst w kolejce — do deduplikacji hovera. */
  private lastQueuedNorm = '';

  constructor(deps: NarratorDeps = {}) {
    this.createAudio = deps.createAudio ?? defaultCreateAudio;
    this.speech = deps.speech ?? defaultSpeech();
    this.storage = deps.storage ?? defaultStorage();
    this.setTimeoutFn = deps.setTimeout ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimeoutFn = deps.clearTimeout ?? ((id) => clearTimeout(id));
    this.load();
  }

  get isOn(): boolean {
    return this.lektorOn;
  }

  setLektor(on: boolean): void {
    this.lektorOn = on;
    this.persist();
    if (!on) this.stop();
  }

  load(): void {
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (raw === null) {
        this.lektorOn = true;
        return;
      }
      if (raw === '0' || raw === 'false') {
        this.lektorOn = false;
        return;
      }
      if (raw === '1' || raw === 'true') {
        this.lektorOn = true;
        return;
      }
      const v = JSON.parse(raw) as { lektorOn?: boolean };
      if (typeof v.lektorOn === 'boolean') this.lektorOn = v.lektorOn;
    } catch {
      this.lektorOn = true;
    }
  }

  private persist(): void {
    this.storage.setItem(STORAGE_KEY, JSON.stringify({ lektorOn: this.lektorOn }));
  }

  /** Zatrzymaj aktualne odtwarzanie i anuluj debounce. */
  stop(): void {
    this.cancelDebounce();
    this.pendingToken += 1;
    this.stopAudio();
    this.speech.cancel();
    this.lastQueuedNorm = '';
  }

  /**
   * Odtwórz frazę po kluczu katalogu.
   * Brak pliku → speechSynthesis z tekstem z katalogu (lub samym kluczem).
   */
  say(key: string, opts: SayOpts = {}): void {
    if (!this.lektorOn) return;
    const text = catalogText(key) ?? key;
    this.enqueue(key, text, opts);
  }

  /**
   * Odtwórz dowolny tekst PL.
   * Jeśli pasuje do katalogu → preferuj plik ogg; inaczej speechSynthesis.
   */
  sayText(text: string, opts: SayOpts = {}): void {
    if (!this.lektorOn) return;
    const cleaned = text?.trim();
    if (!cleaned) return;
    const key = catalogKeyForText(cleaned);
    if (key) {
      this.enqueue(key, catalogText(key) ?? cleaned, opts);
    } else {
      this.enqueue(null, cleaned, opts);
    }
  }

  private enqueue(key: string | null, text: string, opts: SayOpts): void {
    const norm = normalizeLabel(text);
    if (!opts.immediate && norm && norm === this.lastQueuedNorm) {
      // ten sam hover — nie restartuj debounce
      return;
    }
    this.lastQueuedNorm = norm;
    this.cancelDebounce();
    const token = ++this.pendingToken;

    const run = () => {
      if (token !== this.pendingToken) return;
      this.playNow(key, text, token);
    };

    if (opts.immediate) {
      run();
      return;
    }
    this.debounceTimer = this.setTimeoutFn(run, NARRATOR_HOVER_DEBOUNCE_MS);
  }

  private playNow(key: string | null, text: string, token: number): void {
    this.stopAudio();
    this.speech.cancel();

    if (key && !missingFiles.has(key)) {
      const url = ttsUrl(key);
      try {
        const audio = this.createAudio(url);
        this.currentAudio = audio;
        audio.onended = () => {
          if (this.currentAudio === audio) this.currentAudio = null;
        };
        audio.onerror = () => {
          missingFiles.add(key);
          if (this.currentAudio === audio) this.currentAudio = null;
          if (token === this.pendingToken) this.speakFallback(text);
        };
        void audio.play().catch(() => {
          missingFiles.add(key);
          if (this.currentAudio === audio) this.currentAudio = null;
          if (token === this.pendingToken) this.speakFallback(text);
        });
        return;
      } catch {
        missingFiles.add(key);
      }
    }
    this.speakFallback(text);
  }

  private speakFallback(text: string): void {
    this.speech.speak(text, 'pl-PL');
  }

  private stopAudio(): void {
    if (!this.currentAudio) return;
    try {
      this.currentAudio.pause();
      this.currentAudio.currentTime = 0;
    } catch {
      /* ignore */
    }
    this.currentAudio = null;
  }

  private cancelDebounce(): void {
    if (this.debounceTimer != null) {
      this.clearTimeoutFn(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  /** Testy / reset stanu braków plików. */
  resetMissingCache(): void {
    missingFiles.clear();
  }
}

export const narrator = new Narrator();
